/*
 * Greek Search — predictive dropdown data-source swap.
 *
 * Empire's LiveSearch (assets/empire.js) fetches a predictive-search URL marked
 * with `section_id=predictive-search` and injects the returned HTML into the
 * flydown. We wrap window.fetch so that ONE request type is served from our
 * Greek-tuned API instead, returning HTML shaped like the native predictive
 * section. Any failure (or the toggle being off) falls through to the original
 * fetch, so native Shopify search renders exactly as before.
 *
 * Config comes from window.__greekSearch (snippets/greek-search-config.liquid).
 */
(function () {
  'use strict';

  var cfg = window.__greekSearch || {};
  if (!cfg.enabled || !cfg.apiBase || typeof window.fetch !== 'function') {
    return; // disabled or unsupported → leave native behavior untouched
  }

  var API_BASE = String(cfg.apiBase).replace(/\/+$/, '');
  var LOCALE = String(cfg.locale || '');
  // On the English storefront (/en, iso_code "en"...) show the merchant's English
  // titles; on Greek show Greek. Matching is bilingual server-side regardless.
  var IS_ENGLISH = /^en/i.test(LOCALE);
  // Anonymous, first-party search analytics ids. session = per-visitor (persisted);
  // searchId = per rendered dropdown. No PII.
  function uuid() {
    return (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
  }
  function sessionId() {
    var k = 'gs_sid', v;
    try { v = localStorage.getItem(k); if (!v) { v = uuid(); localStorage.setItem(k, v); } }
    catch (e) { v = v || uuid(); }   // private mode / storage blocked
    return v;
  }
  var SESSION_ID = sessionId();
  var currentSearchId = null;   // the search_id of the dropdown currently shown
  var I18N = cfg.i18n || {};
  var TIMEOUT_MS = 1000;
  var origFetch = window.fetch.bind(window);

  function urlOf(input) {
    return typeof input === 'string' ? input : (input && input.url) || '';
  }

  function isPredictiveRequest(url) {
    return url.indexOf('section_id=predictive-search') !== -1;
  }

  function getParam(url, name) {
    var m = url.match(new RegExp('[?&]' + name + '=([^&]*)'));
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function sized(url) {
    if (!url) return '';
    return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'width=140';
  }

  // Greek-aware client-side highlighting. The server highlighter is unreliable
  // for Greek prefix matches, so we bold the matched prefix here instead.
  // fold() strips diacritics (tonos) + lowercases; for Greek/Latin it preserves
  // length (precomposed char -> base + combining mark removed), so the folded
  // index aligns 1:1 with the original, letting us map a match back to it.
  function fold(s) {
    return String(s == null ? '' : s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }
  // Longest folded query-prefix match starting at a word boundary (whitespace).
  // Returns {start, len}; len 0 means no usable match. Shared by highlight() and
  // codeLine() so titles, codes, and SKUs all light up identically.
  function bestPrefixMatch(text, query) {
    text = String(text == null ? '' : text);
    var q = fold(query).trim();
    if (q.length < 2) return { start: -1, len: 0 };
    var ft = fold(text);
    if (ft.length !== text.length) return { start: -1, len: 0 }; // alignment broke -> plain
    var best = -1, bestLen = 0;
    for (var i = 0; i < ft.length; i++) {
      var isWordStart = (i === 0) || /\s/.test(ft[i - 1]);
      if (!isWordStart) continue;
      var n = 0;
      while (n < q.length && ft[i + n] === q[n]) n++;
      if (n > bestLen) { bestLen = n; best = i; }
    }
    if (bestLen < 2) return { start: -1, len: 0 };
    return { start: best, len: bestLen };
  }

  // Bold the matched query-prefix inside any text (title, code, or SKU).
  function highlight(text, query) {
    text = String(text == null ? '' : text);
    var m = bestPrefixMatch(text, query);
    if (m.start < 0 || m.len === 0) return esc(text);
    return esc(text.slice(0, m.start)) + '<mark class="gs-hl">' +
      esc(text.slice(m.start, m.start + m.len)) + "</mark>" +
      esc(text.slice(m.start + m.len));
  }

  // Pick what the "Code:" line shows: the candidate (parent code or a variant
  // SKU) with the longest query-prefix match, bolded. Parent code is listed
  // first so it wins ties (typing 1108 -> code 1108, not a SKU starting 1108).
  // When nothing matches (title-only search) the parent code shows unbolded.
  function codeLine(h, query) {
    var candidates = [];
    if (h.code) candidates.push(h.code);
    if (h.sku) {
      var skus = [].concat(h.sku);
      for (var s = 0; s < skus.length; s++) if (skus[s]) candidates.push(skus[s]);
    }
    if (!candidates.length) return '';
    var best = candidates[0], bestLen = bestPrefixMatch(best, query).len;
    for (var j = 1; j < candidates.length; j++) {
      var n = bestPrefixMatch(candidates[j], query).len;
      if (n > bestLen) { bestLen = n; best = candidates[j]; }
    }
    return '<span class="search-flydown--product-code" style="display:block;' +
      'font-size:0.82em;color:#888;line-height:1.2;overflow:hidden;' +
      'text-overflow:ellipsis;white-space:nowrap;">Code: ' + highlight(best, query) + '</span>';
  }

  // True when the query is an EXACT (case-insensitive) code or variant-SKU of some
  // product hit. The API already ranks such a hit #1, so when this holds we render
  // the products group above collections (see fetch wrapper) — otherwise a stray
  // token (e.g. "3d" split out of "EZHR-20K-3D-20") could float a collection above
  // the precise product the shopper asked for.
  function hasExactCodeMatch(products, query) {
    var q = String(query == null ? '' : query).trim().toLowerCase();
    if (!q) return false;
    return (products || []).some(function (h) {
      if (h.code && String(h.code).toLowerCase() === q) return true;
      if (h.sku) {
        var skus = [].concat(h.sku);
        for (var i = 0; i < skus.length; i++) {
          if (skus[i] && String(skus[i]).toLowerCase() === q) return true;
        }
      }
      return false;
    });
  }

  function buildHtml(hits, terms) {
    // `gs-ac` marker scopes the tighter autocomplete spacing (see
    // greek-search-config.liquid) to our results only — native fallback,
    // the loading placeholder, and the full search page are untouched.
    if (!hits || !hits.length) {
      return '<div class="search-flydown__products-pages-posts gs-ac">' +
        '<p class="search-flydown__no-results">' +
        esc(I18N.no_results || 'No results') + '</p></div>';
    }
    var items = hits.map(function (h) {
      var href = '/products/' + encodeURIComponent(h.handle);
      var displayTitle = (IS_ENGLISH && h.title_en) ? h.title_en : h.title;
      var img = h.image_url
        ? '<img src="' + esc(sized(h.image_url)) + '" alt="' + esc(displayTitle) +
          '" loading="lazy">'
        : '';
      var badge = h.bestseller
        ? '<span class="gs-badge-bestseller" style="' +
          'background-color:#3b82f6;color:#fff;padding:2px 8px;font-size:10px;' +
          'font-weight:600;text-transform:capitalize;line-height:1.2;' +
          'letter-spacing:0.3px;border-radius:4px;white-space:nowrap;">Best Seller</span>'
        : '';
      var code = codeLine(h, terms);
      // Badge is a sibling of product-text (not inside it) so the flex row in
      // greek-search-config.liquid can pin it right while the title truncates.
      return '<li><a class="search-flydown--product" href="' + esc(href) +
        '" aria-label="' + esc(displayTitle) + '">' +
        '<figure class="search-flydown--product-image">' + img + '</figure>' +
        '<div class="search-flydown--product-text">' +
        '<span class="search-flydown--product-title">' +
        highlight(displayTitle, terms) + '</span>' +
        code +
        '</div>' +
        badge +
        '</a></li>';
    }).join('');
    return '<div class="search-flydown__products-pages-posts gs-ac">' +
      '<div class="search-flydown--product-items">' +
      '<h3 class="search-flydown__group-title">' +
      esc(I18N.products_title || 'Products') + '</h3>' +
      '<ul class="search-flydown__group-list">' + items + '</ul>' +
      '</div></div>';
  }

  function buildCollections(cols, terms) {
    if (!cols || !cols.length) return '';
    var items = cols.map(function (c) {
      var href = '/collections/' + encodeURIComponent(c.handle);
      var displayTitle = (IS_ENGLISH && c.title_en) ? c.title_en : c.title;
      var img = c.image_url
        ? '<figure class="search-flydown--product-image"><img src="' + esc(sized(c.image_url)) +
          '" alt="' + esc(displayTitle) + '" loading="lazy"></figure>'
        : '';
      return '<li class="search-flydown__list-item gs-collection-item"><a href="' + esc(href) + '">' +
        img + '<span class="gs-collection-title">' +
        highlight(displayTitle, terms) + '</span></a></li>';
    }).join('');
    return '<div class="search-flydown__queries-collections gs-ac">' +
      '<ul class="search-flydown__group-list">' + items + '</ul></div>';
  }

  function fetchSuggest(terms) {
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, TIMEOUT_MS);
    currentSearchId = uuid();
    var opts = ctrl ? { signal: ctrl.signal } : {};
    opts.headers = { 'X-Search-Session': SESSION_ID, 'X-Search-Id': currentSearchId };
    return origFetch(API_BASE + '/suggest?q=' + encodeURIComponent(terms) +
      (LOCALE ? '&locale=' + encodeURIComponent(LOCALE) : ''), opts)
      .then(function (r) {
        clearTimeout(timer);
        if (!r.ok) throw new Error('api ' + r.status);
        return r.json();
      }, function (e) { clearTimeout(timer); throw e; });
  }

  // Fire-and-forget click beacon. sendBeacon survives the navigation; a plain-string
  // body keeps it a CORS-simple request (no preflight). Never blocks the click.
  function beaconClick(itemType, handle, position) {
    if (!currentSearchId || !handle) return;
    try {
      var body = JSON.stringify({
        search_id: currentSearchId, item_type: itemType,
        handle: handle, position: position
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(API_BASE + '/events', body);
      }
    } catch (e) { /* analytics must never break navigation */ }
  }

  window.fetch = function (input, init) {
    var url = urlOf(input);
    if (!isPredictiveRequest(url)) {
      return origFetch(input, init);
    }
    // Empire may send "filter AND terms"; the user's terms are after the last ' AND '.
    var terms = getParam(url, 'q').split(' AND ').pop().trim();
    if (!terms) {
      return origFetch(input, init);
    }
    return fetchSuggest(terms).then(function (data) {
      var products = data && data.products;
      var collections = data && data.collections;
      // Normally collections lead; but on an exact code/SKU match the precise
      // product (ranked #1 by the API) should sit at the very top, above any
      // collection that merely matched a sub-token of the SKU.
      var productsHtml = buildHtml(products, terms);
      var collectionsHtml = buildCollections(collections, terms);
      var html = hasExactCodeMatch(products, terms)
        ? productsHtml + collectionsHtml
        : collectionsHtml + productsHtml;
      return new Response(html, {
        status: 200,
        headers: { 'Content-Type': 'text/html; charset=utf-8' }
      });
    }).catch(function () {
      // Fallback: native Shopify predictive search (the original request).
      return origFetch(input, init);
    });
  };

  // --- Arrow-key navigation over the rendered results ---
  // Up/Down move a highlight through the collection + product links; Enter opens
  // the highlighted one; Escape clears it. Arrows/Enter aren't search keystrokes,
  // so this doesn't interfere with typing or Empire's debounced search.
  (function installKeyNav() {
    var input = document.querySelector('[data-live-search-input]');
    if (!input) return;
    var ACTIVE = 'gs-active';

    function navItems() {
      var box = document.querySelector('[data-live-search-results]');
      return box ? Array.prototype.slice.call(box.querySelectorAll('a[href]')) : [];
    }
    // Only OUR rendered results (collections first, then products). Used so
    // Enter jumps to the top result; empty when the native fallback rendered,
    // which lets that submit to Shopify's own search page untouched.
    function ourResultLinks() {
      var box = document.querySelector('[data-live-search-results]');
      return box ? Array.prototype.slice.call(box.querySelectorAll('.gs-ac a[href]')) : [];
    }
    function activeIndex(list) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].classList.contains(ACTIVE)) return i;
      }
      return -1;
    }
    function setActive(list, idx) {
      for (var i = 0; i < list.length; i++) list[i].classList.remove(ACTIVE);
      if (idx >= 0 && idx < list.length) {
        list[idx].classList.add(ACTIVE);
        if (list[idx].scrollIntoView) list[idx].scrollIntoView({ block: 'nearest' });
      }
    }

    input.addEventListener('keydown', function (e) {
      var list = navItems();
      if (!list.length) return;
      var idx = activeIndex(list);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(list, idx + 1 >= list.length ? 0 : idx + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(list, idx <= 0 ? list.length - 1 : idx - 1);
      } else if (e.key === 'Enter') {
        var chosen = null, ours = ourResultLinks();
        if (idx >= 0) { chosen = list[idx]; }
        else if (ours.length) { chosen = ours[0]; }
        if (chosen) {
          e.preventDefault();
          var cpos = ours.indexOf(chosen);
          var chref = chosen.getAttribute('href') || '';
          beaconClick(chref.indexOf('/collections/') === 0 ? 'collection' : 'product',
                      decodeURIComponent(chref.split('/').pop() || ''),
                      cpos >= 0 ? cpos + 1 : null);
          window.location.href = chosen.href;
        }
      } else if (e.key === 'Escape') {
        setActive(list, -1);
      }
    });
  })();

  // Delegated click capture on rendered results -> beacon. position is the link's
  // index among our result links (collections first, then products).
  document.addEventListener('click', function (e) {
    var box = document.querySelector('[data-live-search-results]');
    if (!box) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !box.contains(a)) return;
    var links = Array.prototype.slice.call(box.querySelectorAll('.gs-ac a[href]'));
    var pos = links.indexOf(a);
    if (pos < 0) return;
    var href = a.getAttribute('href') || '';
    var isCol = href.indexOf('/collections/') === 0;
    var handle = decodeURIComponent(href.split('/').pop() || '');
    beaconClick(isCol ? 'collection' : 'product', handle, pos + 1);
  }, true);
})();
